import { BadgeProps } from "@mantine/core"
import { ReactNode } from "react"
import { StatusPill, toneOfColor } from "./status-pill"

type StatusLike = {
  code?: string
  color?: string
  sentName?: string
  name?: string
}

type Props = {
  /**
   * Either a resolved status object (with .color and .sentName/.name) from
   * the Redux general reference lists, or a raw color string. When both
   * color and label are passed as separate props, those override the status.
   */
  status?: StatusLike | null
  /** Raw color override — takes precedence over status.color */
  color?: string
  /** Raw label override — takes precedence over status.sentName/name */
  label?: ReactNode
  /** Placeholder rendered when nothing resolves (defaults to "-") */
  placeholder?: string
  /** Passed through to Mantine Badge */
  size?: BadgeProps["size"]
  /** Passed through to Mantine Badge */
  radius?: BadgeProps["radius"]
  /** Passed through to Mantine Badge */
  variant?: BadgeProps["variant"]
}

const FALLBACK_COLOR = "var(--mantine-color-gray-6)"

/**
 * Status pill driven by either a Redux status object (has .code / .color /
 * .sentName) or raw color + label props. Renders the shared StatusPill: the
 * colour only picks the tone (green / yellow / red / blue / gray). size, radius
 * and variant are kept for call-site compatibility and ignored.
 */
export function StatusBadge({ status, color, label, placeholder = "-" }: Props) {
  const resolvedColor = color ?? status?.color ?? FALLBACK_COLOR
  const resolvedLabel = label ?? status?.sentName ?? status?.name ?? placeholder
  return <StatusPill tone={toneOfColor(resolvedColor)}>{resolvedLabel}</StatusPill>
}
